import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { GridCheckbox } from '~/components/GridCheckbox';
import { SelectableGridItem } from '~/components/cards/styles';
import { GridCard } from '~/components/media/card';
import { useLink } from '~/hooks/link';
import type {
  ShowList__Show as Show,
  ShowList__ShowFolder as ShowFolder,
} from '../../queries/queries.generated';
import { ShowCard } from './ShowCard';
import { ShowFolderCard } from './ShowFolderCard';
import { Menu } from './ShowMenu';

interface ShowGridItemProps {
  item: ShowFolder | Show;
  selectedShow?: Show;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  onSelectedShow: (show: Show | undefined) => void;
  selectedIds: GridRowSelectionModel;
}

export const ShowGridItem = ({
  item,
  selectedShow,
  onCheck,
  onSelectedShow,
  selectedIds,
}: ShowGridItemProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const [searchParams] = useSearchParams();
  searchParams.delete('viewId');

  const selectionId = useMemo(
    () => (item.__typename === 'Show' ? item.id : `ShowFolder-${item.id}`),
    [item],
  );
  const selected = selectedIds.includes(selectionId);

  return (
    <SelectableGridItem item xs={12} sm={6} md={3} lg={2} key={selectionId} selected={selected}>
      <GridCard>
        {item.__typename === 'ShowFolder' ? (
          <ShowFolderCard
            showFolder={item}
            selectedShow={selectedShow}
            onSelectedShow={onSelectedShow}
          />
        ) : (
          <>
            <GridCheckbox
              checked={selected}
              inputProps={{ 'aria-label': t('common.actions.select_item', { name: item.name }) }}
              onChange={(event) => {
                if (event.target.checked) {
                  return onCheck([...selectedIds, selectionId]);
                }
                onCheck(selectedIds.filter((x) => x !== selectionId));
              }}
            />
            <ShowCard
              show={item}
              to={{
                ...link(`/shows/${item.showFolderId}/show/${item.id}/design`),
                search: searchParams.toString(),
              }}
            >
              <Menu item={item} selectedShow={selectedShow} onSelectedShow={onSelectedShow} />
            </ShowCard>
          </>
        )}
      </GridCard>
    </SelectableGridItem>
  );
};
